import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { api, type Download } from '../lib/api';
import { bytes, relativ, uhrzeit } from '../lib/format';
import { StatusChip } from './Dashboard';

const PRO_SEITE = 50;

/** Downloads mit Blaettern und Statusfilter. Die alte Seite zeigte stumm die
 *  ersten 50 und liess den Rest unerwaehnt — man konnte nicht wissen, dass
 *  da noch 762 weitere liegen. */
export function Downloads() {
  const [zeilen, setZeilen] = useState<Download[] | null>(null);
  const [seite, setSeite] = useState(0);
  const [status, setStatus] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await api<Download[]>(`/downloads?limit=${PRO_SEITE}&offset=${seite * PRO_SEITE}`);
      setZeilen(d);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [seite]);

  useEffect(() => { void laden(); }, [laden]);

  const sichtbar = (zeilen ?? []).filter((d) => !status || d.status === status);

  return (
    <>
      <div className="kopf">
        <div>
          <h1>Downloads</h1>
          <p>Alles, was AutoGrab an NZBGet übergeben hat — automatisch wie von Hand.</p>
        </div>
        <div className="reihe">
          <Filter size={15} className="schwach" />
          <select className="feld" style={{ width: 190 }} value={status}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="">alle Status</option>
            <option value="completed">fertig</option>
            <option value="downloading">lädt gerade</option>
            <option value="failed">fehlgeschlagen</option>
            <option value="pushed">übergeben</option>
          </select>
        </div>
      </div>

      {fehler && <div className="hinweis hinweis--schlecht" style={{ marginBottom: 14 }}>{fehler}</div>}

      <div className="karte">
        {!zeilen ? (
          <div className="laedt">Lade …</div>
        ) : sichtbar.length === 0 ? (
          <div className="karte__leer">
            {status ? 'Auf dieser Seite nichts mit diesem Status.' : 'Nichts vorhanden.'}
          </div>
        ) : (
          <table className="tabelle">
            <thead>
              <tr>
                <th>Titel</th>
                <th style={{ width: 100 }}>Quelle</th>
                <th style={{ width: 130 }}>Status</th>
                <th className="num" style={{ width: 90 }}>Größe</th>
                <th className="num" style={{ width: 120 }}>Wann</th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((d) => (
                <tr key={d.id}>
                  <td className="titel-zelle">
                    {d.title}
                    {d.season != null && d.episode != null && (
                      <span className="chip chip--neutral" style={{ marginLeft: 8 }}>
                        S{d.season}E{d.episode}
                      </span>
                    )}
                  </td>
                  <td><span className="chip chip--neutral">{d.source ?? '—'}</span></td>
                  <td><StatusChip status={d.status} /></td>
                  <td className="num klein leise">{bytes(d.size_bytes)}</td>
                  <td className="num klein leise" title={uhrzeit(d.downloaded_at)}>
                    {relativ(d.downloaded_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="reihe reihe--verteilt" style={{ marginTop: 14 }}>
        <span className="klein schwach">
          Seite {seite + 1}{status && zeilen ? ` · ${sichtbar.length} von ${zeilen.length} passen zum Filter` : ''}
        </span>
        <div className="reihe">
          <button className="knopf knopf--klein" disabled={seite === 0}
            onClick={() => setSeite((s) => Math.max(0, s - 1))}>
            <ChevronLeft size={15} /> Zurück
          </button>
          <button className="knopf knopf--klein"
            disabled={!zeilen || zeilen.length < PRO_SEITE}
            onClick={() => setSeite((s) => s + 1)}>
            Weiter <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
}
